// Pull-to-refresh gesture hook.
//
// Listens for touch events at the document level. Fires `onRefresh` when
// the user pulls down past `threshold` pixels while the window scroll
// position is at the top.
//
// Design notes:
//   * State that changes during the gesture (touch start Y, current
//     distance) lives in refs — we only call `setState` for the visual
//     indicator. This keeps the effect's listener bind from re-running
//     on every touch-move tick.
//   * Skips when the touch target is inside an editable surface (input,
//     textarea, contentEditable) or a Radix dialog — those scroll
//     independently and need their own gesture handling.
//   * Rubber-banding (delta * 0.5, capped at 1.5×threshold) gives the
//     pull a "stretchy" feel that signals to the user this is a gesture,
//     not a normal scroll.
//   * iOS Safari's native pull-to-refresh is suppressed at the CSS
//     layer via `overscroll-behavior: contain` on <html> (see
//     index.css). Without that, the OS gesture would reload the page
//     and ours would never fire on iPhone.

import { useEffect, useRef, useState } from 'react';

interface Options {
  onRefresh: () => void | Promise<void>;
  /** Distance in px the user must pull past for the refresh to fire. */
  threshold?: number;
  /** Master kill switch — set true to fully disable (e.g. on a route
   *  that doesn't benefit). */
  disabled?: boolean;
}

interface State {
  /** Pixels pulled, post-rubber-banding. 0 when not actively gesturing. */
  pullDistance: number;
  /** True while `onRefresh` is in flight (or its forced visual delay). */
  isRefreshing: boolean;
  /** Pass-through so the wrapper component can render the threshold line. */
  threshold: number;
}

const DEFAULT_THRESHOLD = 80;

function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest('input, textarea, select, [contenteditable="true"]')) return true;
  // Radix dialogs and popovers have their own scroll surfaces we shouldn't
  // hijack.
  if (el.closest('[role="dialog"], [role="menu"], [role="listbox"]')) return true;
  return false;
}

export function usePullToRefresh({
  onRefresh,
  threshold = DEFAULT_THRESHOLD,
  disabled = false,
}: Options): State {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // All mutable per-touch state lives in refs so the effect's listeners
  // bind once and read the latest values via the ref.
  const gestureRef = useRef({
    touchStartY: null as number | null,
    currentDistance: 0,
  });
  const handlerRef = useRef(onRefresh);
  const isRefreshingRef = useRef(false);
  useEffect(() => {
    handlerRef.current = onRefresh;
  });
  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  useEffect(() => {
    if (disabled) return;

    function onTouchStart(e: TouchEvent) {
      if (window.scrollY > 0) return;
      if (isEditableTarget(e.target)) return;
      gestureRef.current.touchStartY = e.touches[0]?.clientY ?? null;
      gestureRef.current.currentDistance = 0;
    }

    function onTouchMove(e: TouchEvent) {
      const startY = gestureRef.current.touchStartY;
      if (startY === null) return;
      // If they scrolled past the top mid-gesture, abandon.
      if (window.scrollY > 0) {
        gestureRef.current.touchStartY = null;
        gestureRef.current.currentDistance = 0;
        setPullDistance(0);
        return;
      }
      const delta = (e.touches[0]?.clientY ?? startY) - startY;
      if (delta > 0) {
        // Rubber-band: pulls feel half as long as the actual finger
        // travel, capped at 1.5× threshold so a frantic pull doesn't
        // shoot the indicator across the screen.
        const distance = Math.min(delta * 0.5, threshold * 1.5);
        gestureRef.current.currentDistance = distance;
        setPullDistance(distance);
        // Block the native scroll only when we're meaningfully into a
        // pull. Small accidental drags (<5px) pass through so taps
        // don't get swallowed.
        if (distance > 5) e.preventDefault();
      }
    }

    async function onTouchEnd() {
      const distance = gestureRef.current.currentDistance;
      gestureRef.current.touchStartY = null;
      gestureRef.current.currentDistance = 0;
      setPullDistance(0);
      if (distance >= threshold && !isRefreshingRef.current) {
        setIsRefreshing(true);
        try {
          await handlerRef.current();
        } finally {
          // Minimum 500ms spinner so a fast refresh doesn't flash.
          window.setTimeout(() => setIsRefreshing(false), 500);
        }
      }
    }

    // touchmove needs passive:false because we conditionally
    // preventDefault. The others stay passive for scroll performance.
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [threshold, disabled]);

  return { pullDistance, isRefreshing, threshold };
}
